import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, type LucideIcon, ShieldCheck } from 'lucide-react';
import type { MfaFactor } from '@lizrd/contracts';
import * as api from '../../lib/api';
import { useAuthStore } from '../../store/auth';
import { Alert } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Modal } from '../ui/modal';
import { Spinner } from '../ui/spinner';
import { useToast } from '../ui/toast';
import { MfaSetupDialog } from './MfaSetupDialog';
import { RecoveryCodesPanel } from './RecoveryCodesPanel';

const labelClass = 'mb-1 block text-xs font-semibold uppercase tracking-wide text-muted';

function factorMeta(f: MfaFactor): { icon: LucideIcon; kind: string } {
  return f.type === 'WEBAUTHN'
    ? { icon: KeyRound, kind: 'Passkey' }
    : { icon: ShieldCheck, kind: 'Authenticator app' };
}

/**
 * Security → Two-factor authentication (Feature 25). Shows the user's factors
 * (authenticator apps + passkeys) and drives enrollment, recovery codes, and
 * removal.
 */
export function MfaCard() {
  const queryClient = useQueryClient();
  const reloadUser = useAuthStore((s) => s.reloadUser);
  const { toast } = useToast();
  const { data: status, isLoading } = useQuery({
    queryKey: ['mfa-status'],
    queryFn: api.getMfaStatus,
  });

  const [setupOpen, setSetupOpen] = useState(false);
  const [newCodes, setNewCodes] = useState<string[] | null>(null);

  async function refresh() {
    await Promise.all([queryClient.invalidateQueries({ queryKey: ['mfa-status'] }), reloadUser()]);
  }

  const addPasskey = useMutation({
    mutationFn: () => api.addPasskey(),
    onSuccess: async (result) => {
      await refresh();
      if (result.recoveryCodes.length > 0) setNewCodes(result.recoveryCodes);
      toast({ title: 'Passkey added', tone: 'success' });
    },
    onError: (err) =>
      toast({
        title: 'Passkey setup didn’t finish',
        description: err instanceof Error ? err.message : undefined,
        tone: 'danger',
      }),
  });

  const regenerate = useMutation({
    mutationFn: api.regenerateRecoveryCodes,
    onSuccess: (result) => {
      setNewCodes(result.recoveryCodes);
      void queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
    },
    onError: (err) =>
      toast({
        title: 'Could not regenerate codes',
        description: err instanceof Error ? err.message : undefined,
        tone: 'danger',
      }),
  });

  const removeFactor = useMutation({
    mutationFn: (id: string) => api.removeMfaFactor(id),
    onSuccess: async () => {
      await refresh();
      toast({ title: 'Removed', tone: 'info' });
    },
    onError: (err) =>
      toast({
        title: 'Could not remove that factor',
        description: err instanceof Error ? err.message : undefined,
        tone: 'danger',
      }),
  });

  const enabled = status?.enabled ?? false;
  const canPasskey = api.passkeysSupported();

  return (
    <Card className="space-y-4 p-6">
      <div className="flex items-center justify-between gap-3">
        <span className={labelClass}>Two-factor authentication</span>
        {!isLoading &&
          (enabled ? (
            <Badge variant="success" dot>
              On
            </Badge>
          ) : (
            <Badge variant="neutral">Off</Badge>
          ))}
      </div>

      <p className="max-w-xl text-xs leading-relaxed text-muted">
        Add a second step at sign-in — an authenticator app or a passkey (Touch ID, Windows Hello, a
        security key) — so a stolen password isn&rsquo;t enough to reach your account.
      </p>

      {isLoading ? (
        <div className="flex h-16 items-center">
          <Spinner size="sm" label="Loading…" />
        </div>
      ) : (
        <div className="space-y-4">
          {enabled && (
            <ul className="space-y-2">
              {status!.factors.map((f) => {
                const meta = factorMeta(f);
                return (
                  <li
                    key={f.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-hairline bg-surface-2 px-3 py-2 text-sm"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      <meta.icon aria-hidden className="h-4 w-4 flex-none text-muted" />
                      <span className="truncate font-medium text-bone">{f.label}</span>
                      <Badge variant="neutral">{meta.kind}</Badge>
                    </div>
                    <div className="flex flex-none items-center gap-3">
                      <span className="hidden text-xs text-muted sm:inline">
                        Added {new Date(f.createdAt).toLocaleDateString()}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFactor.mutate(f.id)}
                        disabled={removeFactor.isPending}
                        className="text-xs text-danger hover:underline disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {enabled &&
            (status!.recoveryCodesRemaining === 0 ? (
              <Alert variant="warning">
                You&rsquo;ve used all your recovery codes. Regenerate a new set so you have a backup
                way in.
              </Alert>
            ) : (
              <p className="text-xs text-muted">
                {status!.recoveryCodesRemaining} recovery code
                {status!.recoveryCodesRemaining === 1 ? '' : 's'} remaining.
              </p>
            ))}

          <div className="flex flex-wrap gap-2">
            <Button
              variant={enabled ? 'outline' : 'primary'}
              size="sm"
              onClick={() => setSetupOpen(true)}
            >
              Add authenticator app
            </Button>
            {canPasskey && (
              <Button
                variant={enabled ? 'outline' : 'primary'}
                size="sm"
                onClick={() => addPasskey.mutate()}
                disabled={addPasskey.isPending}
              >
                {addPasskey.isPending ? 'Waiting for passkey…' : 'Add a passkey'}
              </Button>
            )}
            {enabled && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => regenerate.mutate()}
                disabled={regenerate.isPending}
              >
                {regenerate.isPending ? 'Regenerating…' : 'Regenerate recovery codes'}
              </Button>
            )}
          </div>
        </div>
      )}

      <MfaSetupDialog
        open={setupOpen}
        onClose={() => setSetupOpen(false)}
        onEnabled={() => void refresh()}
      />

      {/* Newly issued recovery codes — shown once. */}
      <Modal
        open={newCodes !== null}
        onClose={() => setNewCodes(null)}
        title={
          <h2 className="font-display text-lg font-bold text-bone">Save your recovery codes</h2>
        }
      >
        {newCodes && (
          <div className="space-y-4">
            <RecoveryCodesPanel codes={newCodes} />
            <Button onClick={() => setNewCodes(null)} className="w-full">
              Done
            </Button>
          </div>
        )}
      </Modal>
    </Card>
  );
}
